import { useState } from 'react';
import styled from 'styled-components';

import { ButtonGroup, ButtonGroupButton } from 'components/ButtonGroup';
import { FormSubmitFunction } from 'components/Form';
import { CmsApiEmailCampaignSignupDoubleBlockData } from 'modules/cms/api/types/blocks/CmsApiEmailCampaignSignupDoubleBlock';
import { CmsEmailCampaignSignup } from 'modules/cms/blocks/EmailCampaignSignupSingle/CmsEmailCampaignSignup';
import { colors, mediaRetina, transition } from 'theme/theme';
import { postJson } from 'utils/http/postJson';

const ToggleButton = styled(ButtonGroupButton)<{ $selected?: boolean }>`
  width: 50%;
  padding: 12px;
  border: none;
  box-shadow: 0 0 0 1px white inset;
  ${mediaRetina} {
    box-shadow: 0 0 0 1.5px white inset;
  }
  overflow: hidden;
  transition: all ${transition};
  &:first-of-type {
    border-radius: 8px 0 0 8px;
  }
  &:last-of-type {
    border-radius: 0 8px 8px 0;
  }
  ${(props) =>
    props.$selected
      ? `
    background: white;
    color: ${colors.brandBlue};
    &:hover, &:focus {
      background: white;
      color: ${colors.brandBlue};
      ${mediaRetina} {
        box-shadow: 0 0 0 1.5px white inset;
      }
    }
  `
      : `
    background: transparent;
    color: white;
    border: 0;
    &:hover, &:focus {
      background: ${colors.brandGreen};
      box-shadow: 0 0 0 1px ${colors.brandGreen} inset;
      ${mediaRetina} {
        box-shadow: 0 0 0 1.5px ${colors.brandGreen} inset;
      }
    }
  `}
`;

type Props = {
  blockId: string;
  data: CmsApiEmailCampaignSignupDoubleBlockData;
};

export function CmsEmailCampaignSignupDoubleBlock({
  blockId,
  data: {
    headline,
    toggleTextOne,
    toggleTextTwo,
    buttonTextOne,
    buttonTextTwo,
    bgImage,
  },
}: Props) {
  const [mode, setMode] = useState('initial');
  const [isOne, setIsOne] = useState(true);

  const onSubmit: FormSubmitFunction<
    CmsApiEmailCampaignSignupDoubleBlockData & { index: number }
  > = ({ values, handleError }) => {
    const postBaseUrl = '/data/website/page-email-signup-double/';
    setMode('submitting');
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-param-reassign
    values.index = isOne ? 0 : 1;
    postJson(postBaseUrl + blockId, values)
      .then(() => {
        setMode('submitted');
      })
      .catch((e) => {
        handleError(e);
        setMode('initial');
      });
  };

  return (
    <CmsEmailCampaignSignup
      disabled={mode === 'submitting'}
      onSubmit={onSubmit}
      headline={headline}
      bgImage={bgImage}
      buttonText={isOne ? buttonTextOne : buttonTextTwo}
      preForm={
        <ButtonGroup mb="10px">
          <ToggleButton
            key="one"
            onClick={() => setIsOne(true)}
            $selected={isOne}
            tabIndex={isOne ? -1 : 0}
            data-qa-id="cms-email-campaign-signup-toggle-one"
          >
            {toggleTextOne}
          </ToggleButton>
          <ToggleButton
            key="two"
            onClick={() => setIsOne(false)}
            $selected={!isOne}
            tabIndex={!isOne ? -1 : 0}
            data-qa-id="cms-email-campaign-signup-toggle-two"
          >
            {toggleTextTwo}
          </ToggleButton>
        </ButtonGroup>
      }
      isSubmitted={mode === 'submitted'}
      qaId="cms-email-campaign-signup-double"
    />
  );
}
